// Firebase Optimizer - Reduziert Datenverbrauch und verbessert Performance
import { ref, get, onValue, query, limitToFirst, limitToLast, orderByKey } from 'firebase/database';
import { database } from '../firebaseConfig';

class FirebaseOptimizer {
  constructor() {
    this.cache = new Map();
    this.activeListeners = new Map();
    this.lastFetchTimes = new Map();
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 Minuten Cache
  }

  // Intelligente Datenbankabfrage mit Caching
  async optimizedGet(path, options = {}) {
    const { 
      useCache = true, 
      maxAge = this.CACHE_DURATION,
      limit = null,
      orderBy = null 
    } = options;

    const cacheKey = `${path}_${JSON.stringify(options)}`;
    const now = Date.now();

    // Prüfe Cache
    if (useCache && this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      const lastFetch = this.lastFetchTimes.get(cacheKey) || 0;
      
      if (now - lastFetch < maxAge) {
        console.log(`🚀 Cache Hit für ${path}`);
        return cached;
      }
    }

    try {
      console.log(`📡 Firebase Query für ${path}`);
      
      let dbRef = ref(database, path);
      
      // Optimierte Queries mit Limits
      if (limit || orderBy) {
        const queryConstraints = [];
        if (orderBy) queryConstraints.push(orderByKey());
        if (limit) queryConstraints.push(limitToFirst(limit));
        
        if (queryConstraints.length > 0) {
          dbRef = query(dbRef, ...queryConstraints);
        }
      }

      const snapshot = await get(dbRef);
      const data = snapshot.exists() ? snapshot.val() : null;

      // Cache aktualisieren
      if (useCache) {
        this.cache.set(cacheKey, data);
        this.lastFetchTimes.set(cacheKey, now);
      }

      return data;
    } catch (error) {
      console.error(`Fehler bei optimizedGet für ${path}:`, error);
      throw error;
    }
  }

  // Optimierte Listener mit Debouncing
  optimizedListener(path, callback, options = {}) {
    const { 
      debounceMs = 1000,
      limit = null,
      onlyNewData = false 
    } = options;

    const listenerKey = `${path}_${JSON.stringify(options)}`;

    // Cleanup vorheriger Listener
    if (this.activeListeners.has(listenerKey)) {
      this.activeListeners.get(listenerKey)();
    }

    let timeoutId = null;
    let lastData = null;

    let dbRef = ref(database, path);
    
    if (limit) {
      dbRef = query(dbRef, limitToFirst(limit));
    }

    const listener = onValue(dbRef, (snapshot) => {
      // Debouncing
      if (timeoutId) clearTimeout(timeoutId);
      
      timeoutId = setTimeout(() => {
        const data = snapshot.exists() ? snapshot.val() : null;
        
        // Prüfe auf tatsächliche Änderungen
        if (onlyNewData && JSON.stringify(data) === JSON.stringify(lastData)) {
          console.log(`⏭️  Keine Änderungen in ${path} - Skip Update`);
          return;
        }
        
        lastData = data;
        console.log(`🔄 Update für ${path}`);
        callback(data);
      }, debounceMs);
    });

    // Listener speichern für Cleanup
    this.activeListeners.set(listenerKey, listener);

    // Cleanup-Funktion zurückgeben
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      listener();
      this.activeListeners.delete(listenerKey);
    };
  }

  // Batch-Updates für mehrere Operationen
  async batchUpdate(updates) {
    const updatePromises = Object.entries(updates).map(([path, value]) => {
      return import('firebase/database').then(({ ref, set }) => {
        return set(ref(database, path), value);
      });
    });

    try {
      await Promise.all(updatePromises);
      
      // Cache invalidieren für betroffene Pfade
      Object.keys(updates).forEach(path => {
        this.invalidateCache(path);
      });
      
      console.log(`✅ Batch Update abgeschlossen für ${Object.keys(updates).length} Pfade`);
    } catch (error) {
      console.error('Fehler bei Batch Update:', error);
      throw error;
    }
  }

  // Cache Management
  invalidateCache(pathPattern = null) {
    if (pathPattern) {
      // Invalidiere spezifische Pfade
      for (const key of this.cache.keys()) {
        if (key.includes(pathPattern)) {
          this.cache.delete(key);
          this.lastFetchTimes.delete(key);
        }
      }
    } else {
      // Invalidiere kompletten Cache
      this.cache.clear();
      this.lastFetchTimes.clear();
    }
    console.log(`🗑️  Cache invalidiert für Pattern: ${pathPattern || 'ALL'}`);
  }

  // Speicher-Management
  cleanupOldCache() {
    const now = Date.now();
    for (const [key, timestamp] of this.lastFetchTimes.entries()) {
      if (now - timestamp > this.CACHE_DURATION * 2) {
        this.cache.delete(key);
        this.lastFetchTimes.delete(key);
      }
    }
  }

  // Statistiken für Monitoring
  getStats() {
    return {
      cacheSize: this.cache.size,
      activeListeners: this.activeListeners.size,
      oldestCache: Math.min(...Array.from(this.lastFetchTimes.values())),
      newestCache: Math.max(...Array.from(this.lastFetchTimes.values()))
    };
  }

  // Cleanup aller Ressourcen
  destroy() {
    // Alle Listener beenden
    for (const cleanup of this.activeListeners.values()) {
      cleanup();
    }
    
    this.cache.clear();
    this.lastFetchTimes.clear();
    this.activeListeners.clear();
    console.log('🧹 FirebaseOptimizer cleanup abgeschlossen');
  }
}

// Singleton Instance
export const firebaseOptimizer = new FirebaseOptimizer();

// Cleanup bei Browser-Close
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    firebaseOptimizer.destroy();
  });
  
  // Regelmäßiger Cache-Cleanup
  setInterval(() => {
    firebaseOptimizer.cleanupOldCache();
  }, 10 * 60 * 1000); // Alle 10 Minuten
}

export default firebaseOptimizer; 